import React from 'react';
import { Autocomplete, AutocompleteItem, Card, Input, Switch, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@nextui-org/react';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../../../../store/reducer-main';
import { Family, FamilyFormData } from '../../../../types/families';
import { createUserFamily, initFamiliesSearchResults, searchFamiliesByNameSurname, searchFamilyByName } from '../../../../service/families';
import { loadInterviewFamily } from '../../../../store/interview/slice';
import { formatDate, ts2Age } from '../../../../helpers/utils';
import Typography from '../../common/Typography';
import Button from '../../common/Button';
import { CAREGIVER_COVERAGE_AVAILABILITY } from '../../../../constants/caregivers';
import FamilyForm from '../forms/FamilyForm';
import TextButton from '../../common/TextButton';
import LoopRoundedIcon from '@mui/icons-material/LoopRounded';
import theme from '../../../theme';
import { toast } from 'react-toastify';
import { getUserCaregiver } from '../../../../service/caregiovers';

interface IInterviewFamilySection {

}

export default function InterviewFamilySection({

}: IInterviewFamilySection) {

  const dispatch = useDispatch();

  const menuRef = React.useRef<HTMLDivElement>(null);
  const nameInputRef = React.useRef<HTMLInputElement>(null);
  const surnameInputRef = React.useRef<HTMLInputElement>(null);

  const { userProfile } = useSelector((state: StoreState) => state.profile);
  const { family } = useSelector((state: StoreState) => state.interview);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [creatingFamily, setCreatingFamily] = React.useState<boolean>(false);
  const [assignedCaregiver, setAssignedCaregiver] = React.useState<string | null>(null);
  // search filters 
  const [name, setName] = React.useState<string>('');
  const [surname, setSurname] = React.useState<string>('');
  const [focused, setFocsued] = React.useState<boolean>(false);
  const [searchResults, setSearchResults] = React.useState<Family[]>([]); 

  React.useEffect(() => {
    init();
    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('click', handleClick);
    }
  }, []);

  const handleClick = (
    event: MouseEvent
  ) => {
    const menu = menuRef.current;
    const nameInput = nameInputRef.current;
    const surnameInput = surnameInputRef.current;
    if (!nameInput || !menu || !surnameInput) return;
    if (nameInput.contains(event.target as Node)) return;
    if (surnameInput.contains(event.target as Node)) return;
    if (menu.contains(event.target as Node)) return;
    setFocsued(false);
  }

  React.useEffect(() => {
    if (family && family.assignedCaregiver && userProfile) {
      getUserCaregiver(userProfile.uid, family.assignedCaregiver).then(c => {
        setAssignedCaregiver(`${c.personalData.name} ${c.personalData.surname}`);
      }).catch(e => {
        setAssignedCaregiver(null);
      });
    } 
    setAssignedCaregiver(null);
  }, [family]);

  const init = async () => {
    if (!userProfile) return;
    const initialResults = await initFamiliesSearchResults(userProfile.uid);
    setSearchResults(initialResults);
  }

  const coverage = React.useMemo(() => {
    if (family?.personalData?.coverage) {
      const coverageValue = CAREGIVER_COVERAGE_AVAILABILITY.find(el => el.key === family.personalData.coverage);
      return coverageValue?.value || '';
    }
    return '';
  }, [family]);

  const onCreateFamily = async (
    familyFormData: FamilyFormData
  ) => {
    try {
      if (!userProfile) return;
      setLoading(true);
      const family = await createUserFamily(userProfile.uid, familyFormData);
      dispatch(loadInterviewFamily(family));
      setCreatingFamily(false);
      setLoading(false);
    } catch (error) {
      toast.error('Si è verificato un errore durante la creazione della famiglia');
      setLoading(false);
    }
  }

  const onCreateDiscard = () => {
    setLoading(false);
    dispatch(loadInterviewFamily(null));
    setCreatingFamily(false);
  }

  const onFamilyDiscard = () => {
    onNameChange('');
    onSurnameChange('');
    dispatch(loadInterviewFamily(null));
    setFocsued(false);
  }

  const onFamilySelect = (
    familyId: string
  ) => {
    console.log(familyId);
    const familyIdx = searchResults.findIndex(el => el.id === familyId);
    const newSelectedFamily = searchResults[familyIdx];
    dispatch(loadInterviewFamily(newSelectedFamily));
    onSurnameChange('');
    onNameChange('');
  }

  /* FILTRI */
  const onNameChange = async (
    value: string
  ) => {
    setName(value);
    if (!userProfile) return;
    if (value.length === 0 && surname.length === 0) {
      init();
    } else {
      const searchRes = await searchFamiliesByNameSurname(userProfile.uid, value, surname);
      setSearchResults(searchRes);
    }
  }

  const onSurnameChange = async (
    value: string
  ) => {
    setSurname(value);
    if (!userProfile) return;
    if (value.length === 0 && name.length === 0) {
      init();
    } else {
      const searchRes = await searchFamiliesByNameSurname(userProfile.uid, name, value);
      setSearchResults(searchRes);
    }
  }

  // TODO: Far diventare un componente in common
  const FamilyValue = ({label, value}: {label: string; value: string}) => (
    <div className='flex flex-col gap-1'>
      <Typography variant='label' colorVariant='textLabel'>{label}</Typography>
      <Typography variant='button' style={{fontWeight: 600}}>{value}</Typography>
    </div>
  )

  return (
    <div className='w-full h-full flex flex-col'>
      <div className='flex justify-center items-center' style={{position: 'relative'}}>
        <Typography style={{fontWeight: 700}}>Famiglia</Typography>
        {
          family && (
            <TextButton 
              style={{
                position: 'absolute',
                right: 0,
              }}
              icon={<LoopRoundedIcon style={{color: theme.color.primary}}/>}
              title='Cambia famiglia'
              onPress={onFamilyDiscard}
              />
          )
        }
      </div>
      {
        !creatingFamily && !family && (
          <div className='flex flex-col grow-1 flex-1 mt-4'>
            <div style={{display: 'flex', gap: theme.sizing.x1}}>
              <div style={{display: 'flex', flex: 1, gap: theme.sizing.x1, position: 'relative'}}>
                <Input 
                  value={name}
                  ref={nameInputRef}
                  placeholder='Nome'
                  onValueChange={onNameChange}
                  onFocus={() => setFocsued(true)}
                  style={{flex: 5}}/>
                <Input 
                  value={surname}
                  ref={surnameInputRef}
                  placeholder='Cognome'
                  onValueChange={onSurnameChange}
                  onFocus={() => setFocsued(true)}
                  style={{flex: 5}}/>
                { focused && (
                  <div 
                  ref={menuRef}  
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'absolute',
                    top: 40 + 8, // input + spacing (gap)
                    left: 0,
                    right: 0,
                    backgroundColor: '#F4F4F5',
                    overflow: 'scroll',
                    borderRadius: theme.sizing.x2,
                    padding: theme.sizing.x2,
                    gap: theme.sizing.x1,
                    maxHeight: 250,
                    zIndex: 10,
                  }}>
                    {
                      searchResults.map((family) => (
                        <div
                          key={family.id}
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            flex: 1,
                            justifyContent: 'space-between',
                            backgroundColor: 'white',
                            padding: theme.sizing.x1,
                            borderRadius: theme.sizing.x1,
                            alignItems: 'center',
                          }}
                          onClick={() => onFamilySelect(family.id)}>
                            <div>
                              <Typography variant='label'>{family.personalData.name} {family.personalData.surname}</Typography>
                              <Typography variant='label' colorVariant='textLabel'>{family.personalData.address}</Typography>
                            </div>
                            <Typography variant='label' colorVariant='textLabel'>
                              {ts2Age(family.personalData.tsDateOfBirth).toString()} Anni
                            </Typography>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
              <Button
                onClick={() => {
                  onFamilyDiscard();
                  setCreatingFamily(true);
                }}
                variant="secondary" 
                title={'Nuova'} />
            </div>
            <div className='w-full h-full flex flex-1 justify-center items-center'>
              <Typography variant='paragraph' className='text-center'>
                Nessuna famiglia selezionata
              </Typography>
            </div>
          </div>
        )
      }
      {
        creatingFamily ? (
          <FamilyForm
            interviewMode
            loading={loading}
            onCancel={onCreateDiscard}
            onSubmit={onCreateFamily}/>
        ) : family && (
            <div className='w-full h-full mt-8'>
              {/* ANAGRAFICA */}
              <div>
                <Typography
                  style={{fontWeight: 700}} 
                  colorVariant='textTertiary'>
                    ANAGRAFICA
                </Typography>
                <div className='flex items-center gap-3 mt-3 justify-between'>
                  <FamilyValue value={family.personalData.name} label='Nome' /> 
                  <FamilyValue value={family.personalData.surname} label='Cognome' /> 
                  <FamilyValue value={ts2Age(family.personalData.tsDateOfBirth).toString()} label='Età' /> 
                  <FamilyValue value={formatDate(family.personalData.tsDateOfBirth)} label='Data di nascita' /> 
                </div>
                <div className='flex items-center gap-8 mt-3 justify-start'>
                  <FamilyValue value={family.personalData.address} label='Indirizzo' /> 
                  <FamilyValue value={family.personalData.telephone} label='Numero di telefono' />  
                </div>
                <div className='flex items-center gap-16 mt-3 justify-start'>
                  <FamilyValue value={family.personalData.weight.toString()} label='Peso' /> 
                  <FamilyValue value={family.personalData.height.toString()} label='Altezza' />  
                </div>
                <div className='flex items-center gap-16 mt-3 justify-start'>
                  <FamilyValue value={coverage} label='Copertura richiesta' /> 
                </div>
              </div>
  
              <div className='mt-8'>
                <Typography
                  style={{fontWeight: 700}} 
                  colorVariant='textTertiary'>
                    BADANTE ASSUNTA
                </Typography>
                <Typography variant='label' style={{fontStyle: !assignedCaregiver ? 'italic' : undefined }}>
                  {assignedCaregiver ?? 'Nessuna'}
                </Typography>
              </div>
  
              <div className='mt-8'>
                <Typography
                  style={{fontWeight: 700}} 
                  colorVariant='textTertiary'>
                    NAZIONALITA'
                </Typography>
                <Typography variant='label' colorVariant='textLabel' className='my-3'>
                  Ogni nazionalità ha le sue caratteristiche, per nostra esperienza non ce n'è realmente una migliore dell'altra. Noi non facciamo differenze e diamo priorità alla valutazione del loro potenziale lavorativo: sia per soft skill, sia per esperienza pregressa. Nel caso abbiate preferenze in merito, ci sono delle nazionalità che volete escludere dalla ricerca ?
                </Typography>
                <Typography variant='button' style={{fontWeight: 500}}>{family.excludedNationalities.join(', ')}</Typography>
              </div>
  
              <div className='mt-8'>
                <Typography
                  style={{fontWeight: 700}} 
                  colorVariant='textTertiary'>
                    CARATTERISTICHE ASSISTENZA
                </Typography>
                <Table removeWrapper className='mt-3'>
                  <TableHeader>
                    <TableColumn>NOME CARATTERISTICA</TableColumn>
                    <TableColumn>NOTE</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {family.traits.map((ft, i) => (
                      <TableRow key={i}>
                        <TableCell className='flex items-center'>
                          <Typography colorVariant='primary' variant='button' style={{fontWeight: 600}}>{ft.trait}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='button' colorVariant='textLabel' style={{
                            fontStyle: 'italic',
                            whiteSpace: "nowrap",
                            maxWidth: "250px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}>{ft.note  && ft.note.length > 0 ? ft.note : 'Nessuna nota'}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table> 
              </div>
  
              <div className='mt-8'>
                <Typography
                  style={{fontWeight: 700}} 
                  colorVariant='textTertiary'>
                    CARATTERE PAZIENTE
                </Typography>
                <Table removeWrapper className='mt-3'>
                  <TableHeader>
                    <TableColumn>CARATTERE</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {family.personalities.map((ft, i) => (
                      <TableRow key={i}>
                        <TableCell className='flex items-center'>
                          <Typography colorVariant='primary' variant='button' style={{fontWeight: 600}}>{ft}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table> 
              </div>
  
              <div className='mt-8'>
                <Typography
                  style={{fontWeight: 700}} 
                  colorVariant='textTertiary'>
                    INFO UTILI PER BADANTE
                </Typography>
                <div className='flex flex-col gap-4 grow-1 mt-3'>
                  {
                    family.usefulInfo.map((ui, i) => (
                      <div className='flex justify-between'>
                        <p key={i}>{ui.name}</p>
                        <Typography>{String(ui.value)}</Typography>
                      </div>
                    ))
                  }
                </div>
              </div>

              <div className='mt-8'>
                <Typography
                  style={{fontWeight: 700}} 
                  colorVariant='textTertiary'>
                    NOTE
                </Typography>
                <div className='flex flex-col gap-4 grow-1 mt-3'>
                  <Typography>{family.note ?? 'Nessuna nota'}</Typography>
                </div>
              </div>
            </div>
          )
      }
    </div>
  )
}